import React from "react";
import { commitMutation, graphql } from "react-relay";

import {
	faClock as faClockInactive,
	faEdit,
	faHashtag,
	faPaperPlane,
	faTrashCan,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faEllipsisV } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faClock as faClockActive } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Sentry from "@sentry/react";
import cx from "classnames";

import { useMedia } from "~/lib/hooks";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { environment } from "~/models";
import { Tooltip, useModals } from "~/ui";
import Menu from "~/ui/menu";

import { DeleteClipModal, RenameClipModal } from "../meetingRecorder/ClipsPanel";
import { openShareClipModal } from "../meetingRecorder/ShareClipModal";
import { openShareMeetingModal } from "../meetingRecorder/ShareMeetingModal";
import { openSendNotesRecordingModal } from "../notes/SendNotesRecordingModal";
import { analyticsProperties } from "./constants";
import { eventEmitter as recordingsEventEmitter } from "./events";
import type { VideoPost } from "./LibraryPostsGrid";

import type { LibraryPostActionsFavouritingMutation } from "~/__generated__/LibraryPostActionsFavouritingMutation.graphql";

import styles from "./LibraryChannelPage.module.css";

type Props = {
	recordingOrClipId: string;
	videoType: "recording" | "clip";
	refetch?: boolean;
};

export function addOrRemoveRecordingOrClipFromFavourites({
	newIsFavourite,
	recordingOrClipId,
	videoType,
}: Props & { newIsFavourite: boolean }) {
	commitMutation<LibraryPostActionsFavouritingMutation>(environment, {
		mutation: graphql`
			mutation LibraryPostActionsFavouritingMutation($recordingOrClipId: ID!, $isFavourite: Boolean!) {
				addOrRemoveRecordingOrClipFromFavourites(recordingOrClipId: $recordingOrClipId, isFavourite: $isFavourite) {
					recordingOrClip {
						__typename

						... on MeetingRecording {
							id
							isFavourite
						}

						... on MeetingRecordingClip {
							id
							isFavourite
						}
					}
				}
			}
		`,
		variables: {
			recordingOrClipId,
			isFavourite: newIsFavourite,
		},
		optimisticUpdater: store => {
			const node = store.get(recordingOrClipId);
			if (!node) {
				return;
			}

			node.setValue(newIsFavourite, "isFavourite");
		},
		onCompleted: (response, errors) => {
			if (errors) {
				Sentry.captureException("Failed to add clip or recording to favourites.", {
					extra: {
						videoType,
						relayErrors: errors,
					},
				});
				return;
			}

			const responseData = response.addOrRemoveRecordingOrClipFromFavourites?.recordingOrClip;
			if (responseData?.__typename === "MeetingRecording" || responseData?.__typename === "MeetingRecordingClip") {
				dispatchAnalyticsTrack(`Recording or clip ${responseData.isFavourite ? "added" : "removed"} from favourites`, {
					fingerprint: analyticsProperties.fingerprint,
					videoType,
				});
			}

			recordingsEventEmitter.emit("Recordings.refetch");
		},
	});
}

export function removeXWCRecordingFromLibrary({ recordingId }: { recordingId: string }) {
	commitMutation(environment, {
		mutation: graphql`
			mutation LibraryPostActionsRemoveXWCRecordingFromLibraryMutation($recordingId: ID!) {
				removeXwcRecordingFromLibrary(recordingId: $recordingId) {
					ok
				}
			}
		`,
		variables: { recordingId },
		onCompleted: (response, errors) => {
			if (errors) {
				Sentry.captureException("Failed to remove XWC recording from library.", {
					extra: {
						relayErrors: errors,
					},
				});
				return;
			}

			dispatchAnalyticsTrack("recording removed from library", {
				fingerprint: analyticsProperties.fingerprint,
			});
			recordingsEventEmitter.emit("Recordings.refetch");
		},
	});
}

export function FavouriteButton({ isFavourite, recordingOrClipId, videoType }: Props & { isFavourite: boolean }) {
	const icon = <FontAwesomeIcon icon={isFavourite ? faClockActive : faClockInactive} />;

	const toggleIsFavourite = React.useCallback(
		(event: React.MouseEvent<HTMLButtonElement>) => {
			event.stopPropagation();

			addOrRemoveRecordingOrClipFromFavourites({ newIsFavourite: !isFavourite, recordingOrClipId, videoType });
		},
		[isFavourite, recordingOrClipId, videoType],
	);

	const supportsHover = useMedia("(hover: hover)");

	return (
		<Tooltip
			placement="top"
			content={`${isFavourite ? "Remove from" : "Add to"} Watch later`}
			disabled={!supportsHover}
		>
			<button
				className={cx(styles.videoPreviewFavouriteButton, styles.showOnHover, { [styles.alwaysVisible]: isFavourite })}
				onClick={toggleIsFavourite}
			>
				{icon}
			</button>
		</Tooltip>
	);
}

export default function RecordingActions({ video, accountID }: { video: VideoPost; accountID: string }) {
	const supportsHover = useMedia("(hover: hover)");
	const stopPropagation = React.useCallback(e => e?.stopPropagation(), []);

	const onSendClick = React.useCallback(() => {
		switch (video.type) {
			case "recording": {
				return video.note?.id ? openSendNotesRecordingModal(video.note.id) : null;
			}
			case "clip": {
				return video.viewerCanEdit ? openShareClipModal(video.id, "Send") : null;
			}
			default:
				return;
		}
	}, [video]);

	const onShareClick = React.useCallback(() => {
		switch (video.type) {
			case "recording": {
				return video.note?.id ? openShareMeetingModal(video.note.id, "Share") : null;
			}
			case "clip": {
				return video.viewerCanEdit ? openShareClipModal(video.id, "Share") : null;
			}
			default:
				return;
		}
	}, [video]);

	const modals = useModals();

	const onRenameClip = React.useCallback(() => {
		if (video.type !== "clip" || !video.viewerCanEdit) return;

		modals.open(({ close }) => <RenameClipModal onClose={close} clip={video} />);
	}, [modals, video]);

	const onDeleteClip = React.useCallback(() => {
		if (video.type !== "clip" || !video.viewerCanEdit) return;

		modals.open(({ close }) => <DeleteClipModal onClose={close} clip={video} />);
	}, [modals, video]);

	const onRemoveRecording = React.useCallback(() => {
		if (video.type !== "recording" || video.ownerAccount.id === accountID) return;

		removeXWCRecordingFromLibrary({ recordingId: video.id });
	}, [video, accountID]);

	if (video.type === "clip" && !video.viewerCanEdit) {
		return null;
	}

	let menuItems = [];

	if ((video.type === "recording" && video.viewerCanDelete) || (video.type === "clip" && video.viewerCanEdit)) {
		menuItems.push(
			<Menu.Item
				key="menu-send"
				onClick={stopPropagation}
				onKeyUp={stopPropagation}
				icon={faPaperPlane}
				onSelect={onSendClick}
			>
				Send via email
			</Menu.Item>,
			<Menu.Item
				key="menu-share"
				onClick={stopPropagation}
				onKeyUp={stopPropagation}
				icon={faHashtag}
				onSelect={onShareClick}
			>
				{video.type === "clip" && "Share with channel"}
				{video.type === "recording" && "Share with…"}
			</Menu.Item>,
		);
	}

	if (video.type !== "recording") {
		menuItems.push(
			<Menu.Item
				key="menu-rename-clip"
				onClick={stopPropagation}
				onKeyUp={stopPropagation}
				icon={faEdit}
				onSelect={onRenameClip}
			>
				Rename
			</Menu.Item>,
		);
	}

	if (video.type === "clip") {
		menuItems.push(
			<Menu.Separator key="menu-separator-1" />,
			<Menu.Item
				key="menu-delete-clip"
				onClick={stopPropagation}
				onKeyUp={stopPropagation}
				icon={faTrashCan}
				onSelect={onDeleteClip}
				destructive
			>
				<span>Delete</span>
			</Menu.Item>,
		);
	}

	if (video.type === "recording" && video.ownerAccount.id !== accountID) {
		menuItems.push(<Menu.Separator key="menu-separator-2" />);
		menuItems.push(
			<Menu.Item
				key="menu-remove-recording"
				onClick={stopPropagation}
				onKeyUp={stopPropagation}
				icon={faTrashCan}
				destructive
				onSelect={onRemoveRecording}
			>
				Remove
			</Menu.Item>,
		);
	}

	if (menuItems.length === 0) {
		return null;
	}

	return (
		<Menu mobileHeaderTitle="More actions">
			<Tooltip placement="top" content="More options" disabled={!supportsHover}>
				<Menu.Trigger asChild>
					<button
						className={cx(styles.videoPreviewMoreActionsButton, styles.showOnHover)}
						onClick={e => e.stopPropagation()}
					>
						<FontAwesomeIcon icon={faEllipsisV} />
					</button>
				</Menu.Trigger>
			</Tooltip>

			<Menu.Content align="end">{menuItems}</Menu.Content>
		</Menu>
	);
}
